import React from 'react';
import { motion } from 'framer-motion';
import { Search, PenTool, LineChart, Target } from 'lucide-react';

const steps = [
  {
    icon: Search,
    title: 'Strategic Analysis',
    description: 'AI assesses your business operations, identifying profit gaps and efficiency opportunities.'
  },
  {
    icon: PenTool,
    title: 'Custom Planning',
    description: 'Tailored strategies based on your industry, goals, and market conditions.'
  },
  {
    icon: LineChart,
    title: 'Expert Execution Guidance',
    description: 'Daily AI insights help you make the right moves and stay on track.'
  },
  {
    icon: Target,
    title: 'Ongoing Optimization',
    description: 'We track progress and hold you accountable for achieving your business goals.'
  }
];

const springConfig = {
  type: "spring",
  stiffness: 50,
  damping: 20,
  mass: 1
};

export const HowItWorks = () => {
  return (
    <section className="relative py-20 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ...springConfig }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-amber-200 to-amber-400 text-transparent bg-clip-text">
              How It Works
            </span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Our proven process helps businesses achieve sustainable growth through AI-powered insights and expert guidance.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2, duration: 1, ...springConfig }}
              className="relative"
            >
              <motion.div
                whileHover={{ y: -5, scale: 1.02 }}
                transition={{ duration: 0.8, ...springConfig }}
                className="h-full p-6 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 backdrop-blur-sm group"
              >
                <div className="relative">
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.8, ...springConfig }}
                    className="w-12 h-12 mb-4 bg-amber-400/10 rounded-lg flex items-center justify-center group-hover:bg-amber-400/20 transition-colors duration-700"
                  >
                    <step.icon className="w-6 h-6 text-amber-400" />
                  </motion.div>
                  <div className="absolute -left-4 -top-4 w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center text-black font-bold">
                    {index + 1}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-gray-400">{step.description}</p>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};